import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, withFetch, HttpClient } from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation } from '@azure/msal-browser';
import { MsalInterceptor, MSAL_INSTANCE, MsalInterceptorConfiguration, MsalGuardConfiguration, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { routes } from './app/app.routes';
import { LoaderInterceptorService } from './app/shared/services/interceptors/http-interceptors/loader-interceptor.service';
import { HttpInterceptorsService } from './app/shared/services/interceptors/http-interceptors/http-interceptors.service';
import { provideMarkdown } from 'ngx-markdown';

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1; // Remove this line to use Angular Universal

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.AUTH_CONFIG.auth.clientId,
      authority: environment.AUTH_CONFIG.auth.authority,
      redirectUri: environment.AUTH_CONFIG.auth.redirectUri,
      postLogoutRedirectUri: environment.AUTH_CONFIG.auth.postLogoutRedirectUri
    },
    cache: {
      // Use the storage location declared in environment config (session storage)
      // instead of local storage, so auth tokens do not persist indefinitely in
      // browser storage accessible to any script on the page (PT-WEB-12).
      cacheLocation: (environment.AUTH_CONFIG.cache?.cacheLocation as BrowserCacheLocation) || BrowserCacheLocation.SessionStorage,
      claimsBasedCachingEnabled: true,
      storeAuthStateInCookie: isIE,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.AUTH_RESOURCE_LINK.users, environment.API_CONFIG_SCOPE);
  protectedResourceMap.set(environment.AUTH_RESOURCE_LINK.self, environment.API_CONFIG_SCOPE);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.API_CONFIG_SCOPE]
    },
    loginFailedRoute: '/login-failed'
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule, MatDialogModule, MatSnackBarModule),
    provideNoopAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideRouter(routes),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorsService,
      multi: true
    },
    {
      provide: REMOVE_STYLES_ON_COMPONENT_DESTROY,
      useValue: false
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideMarkdown({ loader: HttpClient }),
  ]
};
